import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import RobotConnectingDialog from "components/control/settings/RobotConnectingDialog";
import RobotNetworkConfigSheet from "components/control/settings/RobotNetworkConfigSheet";
import RobotProfileCard from "components/control/settings/RobotProfileCard";
import {
    FALLBACK_CATALOG,
    PROFILE_ORDER,
    buildDefaultDrafts,
    resolveDefaults,
    stringifyParams,
    type RobotProfileDraft,
} from "lib/consts/robotProfiles";
import { waitForRobotLinkConnected } from "lib/robotLinkFeedback";
import {
    connectRobotProfile,
    deleteRobotProfile,
    getRobotProfile,
    getRobotProfileBaseUrl,
    getRobotProfiles,
    saveRobotProfile,
    type RobotConnectionConfig,
    type RobotEthernetStatus,
    type RobotProfileCatalogEntry,
    type RobotProfileId,
} from "lib/robotProfileApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import { useRobotProfileLiveness } from "providers/RobotProfileLivenessProvider";

/** Never show raw ROS launch dumps / supervisor exit codes in the settings UI. */
function formatRobotStatusError(raw: string, t: TFunction): string {
    const text = raw.replace(/\u001b\[[0-9;]*m/g, "").trim();
    if (!text) return "";

    const nodeFailed = text.match(/Node failed:\s*([^\n\r]+)/i);
    if (nodeFailed?.[1]) return nodeFailed[1].trim().slice(0, 240);

    const bridgeExitedCleanly =
        /Robot bridge stopped unexpectedly/i.test(text) ||
        /bridge process exited/i.test(text);

    const looksLikeLaunchDump =
        text.includes("\n") ||
        text.length > 280 ||
        /\[INFO\]|\[FATAL\]|\[ERROR\]|gbw_node|ros2_ws/i.test(text);

    if (bridgeExitedCleanly || looksLikeLaunchDump) {
        return t("robot.notConnected", {
            defaultValue:
                "Robot is not connected. Check that it is powered on and reachable, then try Connect again.",
        });
    }
    return text.slice(0, 240);
}

const RobotSettingsPanel = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { selectedARC, controlSocket } = useARC();
    const { addToast } = useToast();
    const { setProfileSnapshot, refreshProfileStatus, robotLinkConnected } =
        useRobotProfileLiveness();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    const baseUrl = useMemo(
        () => getRobotProfileBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
    const [drafts, setDrafts] = useState<Record<RobotProfileId, RobotProfileDraft>>(() =>
        buildDefaultDrafts(FALLBACK_CATALOG)
    );
    const [configuringProfile, setConfiguringProfile] = useState<RobotProfileId | null>(null);
    const [activeProfile, setActiveProfile] = useState<RobotProfileId | null>(null);
    const [profileRunning, setProfileRunning] = useState(false);
    const [connectionState, setConnectionState] = useState<string>("idle");
    const [lastError, setLastError] = useState<string>("");
    const [ethernetStatus, setEthernetStatus] = useState<RobotEthernetStatus>(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [connectingOpen, setConnectingOpen] = useState(false);
    const connectAbortRef = useRef<AbortController | null>(null);

    const updateDraft = useCallback((profile: RobotProfileId, next: RobotProfileDraft) => {
        setDrafts((prev) => ({ ...prev, [profile]: next }));
    }, []);

    const refreshData = useCallback(async () => {
        if (!baseUrl) return;
        setLoading(true);
        try {
            const [profilesRes, profileRes] = await Promise.all([
                getRobotProfiles(baseUrl),
                getRobotProfile(baseUrl),
            ]);

            const mappedCatalog = { ...FALLBACK_CATALOG } as Record<
                RobotProfileId,
                RobotProfileCatalogEntry
            >;
            for (const [key, value] of Object.entries(profilesRes.profiles || {})) {
                if (!PROFILE_ORDER.includes(key as RobotProfileId)) continue;
                const id = key as RobotProfileId;
                mappedCatalog[id] = {
                    ...FALLBACK_CATALOG[id],
                    ...value,
                    methods: value.methods?.length ? value.methods : FALLBACK_CATALOG[id].methods,
                    default_params: {
                        ...(FALLBACK_CATALOG[id].default_params || {}),
                        ...stringifyParams(value.default_params),
                    },
                    method_params: {
                        ...(FALLBACK_CATALOG[id].method_params || {}),
                        ...Object.fromEntries(
                            Object.entries(value.method_params || {}).map(([method, methodParams]) => [
                                method,
                                stringifyParams(methodParams),
                            ])
                        ),
                    },
                };
            }
            setCatalog(mappedCatalog);

            const nextDrafts = buildDefaultDrafts(mappedCatalog);
            const active = profileRes.profile?.active as RobotProfileId | null | undefined;
            const method = profileRes.profile?.connection?.method;
            const nextParams = stringifyParams(profileRes.profile?.connection?.params || undefined);
            const running = Boolean(profileRes.profile?.running);
            setProfileRunning(running);

            if (active && PROFILE_ORDER.includes(active)) {
                const defaults = resolveDefaults(mappedCatalog, active, method);
                nextDrafts[active] = {
                    method: method || defaults.method,
                    params: { ...defaults.params, ...nextParams },
                };
                setActiveProfile(running ? active : null);
            } else {
                setActiveProfile(null);
            }
            setDrafts(nextDrafts);

            setConnectionState(profileRes.status?.state || "idle");
            setLastError(profileRes.status?.last_error || "");
            setEthernetStatus(profileRes.ethernet ?? null);
        } catch (error) {
            const message = error instanceof Error ? error.message : t("robot.loadFailed");
            addToast({ type: "error", message });
            setEthernetStatus(null);
        } finally {
            setLoading(false);
        }
    }, [addToast, baseUrl, t]);

    useEffect(() => {
        if (!baseUrl) return;
        void refreshData();
    }, [baseUrl, refreshData]);

    const onSaveConfig = useCallback(async () => {
        if (!baseUrl || !configuringProfile || busy || connectingOpen) return;
        const draft = drafts[configuringProfile];
        if (!draft) return;
        setBusy(true);
        try {
            const payload: RobotConnectionConfig = {
                method: draft.method,
                params: draft.params,
            };
            await saveRobotProfile(baseUrl, configuringProfile, payload);
            await refreshData();
            addToast({
                type: "success",
                message: t("robot.configSaved", {
                    profile: t(`robot.options.${configuringProfile}.title`),
                    defaultValue: "{{profile}} settings saved.",
                }),
            });
            setConfiguringProfile(null);
        } catch (error) {
            addToast({
                type: "error",
                message: error instanceof Error ? error.message : t("robot.saveFailed"),
            });
            await refreshData();
        } finally {
            setBusy(false);
        }
    }, [
        addToast,
        baseUrl,
        busy,
        configuringProfile,
        connectingOpen,
        drafts,
        refreshData,
        t,
    ]);

    const onConnect = useCallback(
        async (profile: RobotProfileId) => {
            if (!baseUrl || busy || connectingOpen) return;
            const draft = drafts[profile];
            if (!draft) return;
            const ac = new AbortController();
            connectAbortRef.current = ac;
            setConnectingOpen(true);
            setBusy(true);
            try {
                const payload: RobotConnectionConfig = {
                    method: draft.method,
                    params: draft.params,
                };
                const res = await connectRobotProfile(baseUrl, profile, payload, ac.signal);
                setConnectionState(res.status?.state || "running");
                setLastError(res.status?.last_error || "");
                setEthernetStatus(res.ethernet ?? null);

                await waitForRobotLinkConnected(controlSocket, { signal: ac.signal });

                setActiveProfile(profile);
                setProfileRunning(true);
                setProfileSnapshot({ running: true, activeProfile: profile });
                setConnectingOpen(false);
                await refreshData();
                void refreshProfileStatus();
                addToast({
                    type: "success",
                    message: t("robot.saved", {
                        profile: t(`robot.options.${profile}.title`),
                    }),
                });
            } catch (error) {
                const isAbort =
                    (error instanceof DOMException && error.name === "AbortError") ||
                    (error instanceof Error && error.message === "cancelled");
                const isTimeout = error instanceof Error && error.message === "timeout";
                if (isAbort) {
                    try {
                        await deleteRobotProfile(baseUrl);
                    } catch {
                        // best-effort cancellation
                    }
                    setActiveProfile(null);
                    setProfileRunning(false);
                    setProfileSnapshot({ running: false, activeProfile: null });
                }
                if (!isAbort) {
                    addToast({
                        type: "error",
                        message: isTimeout
                            ? t("robot.connectNoFeedback", {
                                  defaultValue:
                                      "No robot link feedback. Connection timed out.",
                              })
                            : error instanceof Error
                              ? formatRobotStatusError(error.message, t) ||
                                t("robot.connectFailed")
                              : t("robot.connectFailed"),
                    });
                } else {
                    addToast({ type: "info", message: t("robot.connectCancelled") });
                }
                await refreshData();
                void refreshProfileStatus();
                setConnectingOpen(false);
            } finally {
                connectAbortRef.current = null;
                setBusy(false);
            }
        },
        [addToast, baseUrl, busy, connectingOpen, controlSocket, drafts, refreshData, refreshProfileStatus, setProfileSnapshot, t]
    );

    const onCancelConnect = useCallback(() => {
        connectAbortRef.current?.abort();
    }, []);

    useEffect(() => {
        if (!baseUrl) return;
        const polling =
            connectionState === "reconnecting" ||
            connectionState === "switching" ||
            busy ||
            connectingOpen;
        if (!polling) return;
        const id = window.setInterval(() => {
            void (async () => {
                try {
                    const profileRes = await getRobotProfile(baseUrl);
                    setConnectionState(profileRes.status?.state || "idle");
                    setLastError(profileRes.status?.last_error || "");
                    setEthernetStatus(profileRes.ethernet ?? null);
                    setProfileRunning(Boolean(profileRes.profile?.running));
                    const active = profileRes.profile?.active as RobotProfileId | null | undefined;
                    // Don't mark Active on cards while the connecting dialog is open —
                    // wait until /state/connected succeeds and the dialog closes.
                    if (connectingOpen) {
                        return;
                    }
                    if (active && PROFILE_ORDER.includes(active) && profileRes.profile?.running) {
                        setActiveProfile(active);
                    } else if (!profileRes.profile?.running) {
                        setActiveProfile(null);
                    }
                } catch {
                    // ignore transient poll errors
                }
            })();
        }, 2000);
        return () => window.clearInterval(id);
    }, [baseUrl, busy, connectingOpen, connectionState]);

    const onDisconnect = useCallback(async () => {
        if (!baseUrl || busy || connectingOpen) return;
        setBusy(true);
        try {
            await deleteRobotProfile(baseUrl);
            setActiveProfile(null);
            setProfileRunning(false);
            setProfileSnapshot({ running: false, activeProfile: null });
            await refreshData();
            void refreshProfileStatus();
            addToast({ type: "success", message: t("robot.disconnected") });
        } catch (error) {
            addToast({
                type: "error",
                message: error instanceof Error ? error.message : t("robot.disconnectFailed"),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, baseUrl, busy, connectingOpen, refreshData, refreshProfileStatus, setProfileSnapshot, t]);

    const configuringDraft = configuringProfile ? drafts[configuringProfile] : null;
    const actionsDisabled = !baseUrl || busy || loading;

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <div>
                <p className="text-h4 font-semibold text-white">{t("robot.title")}</p>
                <p className="mt-1 text-h5 text-black40">{t("robot.description")}</p>
                {!baseUrl ? (
                    <p className="mt-2 text-h5 text-black60">{t("robot.connectFirst")}</p>
                ) : null}
                <p className="mt-2 text-h5 text-black40">
                    {t("robot.connectionState")}:{" "}
                    {t(`robot.status.${connectionState}`, { defaultValue: connectionState })}
                </p>
                {lastError ? (
                    <p className="mt-1 text-h5 text-red-400">
                        {formatRobotStatusError(lastError, t)}
                    </p>
                ) : null}
            </div>

            <div
                className="flex flex-row gap-4 overflow-x-auto pb-2"
                role="list"
                aria-label={t("robot.listboxAriaLabel")}
            >
                {PROFILE_ORDER.map((profileId) => (
                    <div key={profileId} className="w-56 shrink-0" role="listitem">
                        <RobotProfileCard
                            profileId={profileId}
                            isSelected={
                                !connectingOpen &&
                                profileRunning &&
                                activeProfile === profileId
                            }
                            isConnected={
                                !connectingOpen &&
                                profileRunning &&
                                activeProfile === profileId &&
                                robotLinkConnected === true
                            }
                            disabled={actionsDisabled}
                            connecting={connectingOpen}
                            onConnect={() => void onConnect(profileId)}
                            onDisconnect={() => void onDisconnect()}
                            onConfigure={() => setConfiguringProfile(profileId)}
                        />
                    </div>
                ))}
            </div>

            <RobotNetworkConfigSheet
                visible={configuringProfile != null}
                profileId={configuringProfile}
                catalog={catalog}
                draft={configuringDraft}
                ethernetStatus={ethernetStatus}
                busy={busy || connectingOpen}
                onClose={() => {
                    if (!busy && !connectingOpen) setConfiguringProfile(null);
                }}
                onChangeMethod={(method) => {
                    if (!configuringProfile) return;
                    const resolved = resolveDefaults(catalog, configuringProfile, method);
                    const current = drafts[configuringProfile];
                    // Keep operator-entered values (esp. username/password) when
                    // switching ethernet ↔ network so secrets are not wiped.
                    const merged = { ...resolved.params };
                    if (current?.params) {
                        for (const key of Object.keys(merged)) {
                            const prev = current.params[key];
                            if (prev != null && String(prev).length > 0) {
                                merged[key] = prev;
                            }
                        }
                    }
                    updateDraft(configuringProfile, {
                        method: resolved.method,
                        params: merged,
                    });
                }}
                onChangeParam={(key, value) => {
                    if (!configuringProfile) return;
                    const current = drafts[configuringProfile];
                    if (!current) return;
                    updateDraft(configuringProfile, {
                        ...current,
                        params: { ...current.params, [key]: value },
                    });
                }}
                onSave={() => void onSaveConfig()}
            />

            <RobotConnectingDialog visible={connectingOpen} onCancel={onCancelConnect} />
        </div>
    );
};

export default RobotSettingsPanel;
